import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';
import pricingHero from '../../assets/pricing_hero.jpg';

export default function Pricing() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://neurasites.ai';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'OfferCatalog',
    name: 'NeuraSites AI Pricing Plans',
    description:
      'Choose between AI Launch and AI Growth pricing plans from NeuraSites AI for professional website development, with ecommerce available as an add-on.',
    url: `${siteUrl}/pricing`,
    itemListElement: [
      {
        '@type': 'Offer',
        name: 'AI Launch',
        price: '999.00',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        category: 'Website Development Service',
        description:
          'One-time package for a professional, custom 5-page website with AI-generated, human-edited copy and bespoke imagery.'
      },
      {
        '@type': 'Offer',
        name: 'AI Growth',
        price: '250.00',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        category: 'Website Development Service',
        description:
          'Monthly service that includes the full AI Launch package plus hosting, security, content, analytics, and ongoing updates.'
      },
      {
        '@type': 'Offer',
        name: 'Ecommerce Add-On',
        price: '1499.00',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        category: 'Ecommerce Add-On',
        description:
          'Add ecommerce to your website with product setup, payments, and a conversion-focused checkout flow.'
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
      <SEOHead
        title="Pricing | NeuraSites AI"
        description="Explore NeuraSites AI pricing. Choose between our AI Launch one-time package or AI Growth subscription for ongoing website success."
        keywords="website pricing, AI website packages, web design pricing, NeuraSites AI pricing"
        ogTitle="NeuraSites AI Pricing Plans"
        ogDescription="Pick the perfect plan for your business. Launch fast with AI Launch or grow with our all-inclusive AI Growth plan."
        canonicalUrl={`${siteUrl}/pricing`}
        structuredData={structuredData}
      />

      <Header />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden brand-soft-surface">
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute inset-0 bg-center bg-cover"
              style={{ backgroundImage: `url(${pricingHero})` }}
              aria-hidden="true"
            />
            <div className="absolute inset-0 brand-soft-overlay" />
          </div>
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              AI Speed, Human Expertise.
              <span className="block brand-heading-accent">
                Your New Website, Made Simple.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8">
              You need a professional website that gets results, but you don&apos;t have time for AI headaches or
              agency delays. We combine lightning-fast artificial intelligence with 15 years of expert human programming
              to deliver custom, high-performance websites in weeks—not months.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/vibe-check"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg"
              >
                Start Your Project
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center border-2 border-blue-600 text-blue-600 dark:text-blue-400 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-blue-600 hover:text-white dark:hover:text-white transition-colors"
              >
                Talk With Our Team
              </Link>
            </div>
          </div>
        </section>

        {/* Plan Selector */}
        <section className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Choose Your Perfect Plan
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                We offer two simple paths to get your business online. One to launch, and one to grow.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* AI Launch */}
              <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-3xl shadow-xl overflow-hidden">
                <div className="relative h-44 bg-gray-100 dark:bg-gray-700">
                  <img
                    src="/pricing-cards/2026-02-28-21-02-00-ai-launch.webp"
                    alt="AI Launch infographic showing website launch process and milestones"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 space-y-6">
                  <div>
                    <p className="text-sm uppercase tracking-wide text-blue-600 dark:text-blue-400 font-semibold">
                      AI Launch
                    </p>
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-white mt-2">AI Launch</h3>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-2">$999 One-Time Fee</p>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300">
                    Our powerful, one-time package to get a professional, custom website online—fast. We handle all the
                    technical work so you can get back to running your business.
                  </p>
                  <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                    <li className="flex items-center">
                      <i className="ri-checkbox-circle-line text-blue-600 dark:text-blue-400 mr-3 text-xl" />
                      <span>Custom 5-Page Website (Home, About, Services, Blog, Contact)</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-checkbox-circle-line text-blue-600 dark:text-blue-400 mr-3 text-xl" />
                      <span>AI-Generated, Human-Edited Copy tailored to your brand voice</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-checkbox-circle-line text-blue-600 dark:text-blue-400 mr-3 text-xl" />
                      <span>Bespoke AI-Generated Imagery to stand out from stock photos</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-checkbox-circle-line text-blue-600 dark:text-blue-400 mr-3 text-xl" />
                      <span>Mobile-friendly, responsive design for every device</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-checkbox-circle-line text-blue-600 dark:text-blue-400 mr-3 text-xl" />
                      <span>Simple contact form to capture new leads from day one</span>
                    </li>
                  </ul>
                  <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 rounded-2xl p-6 text-center">
                    <p className="text-base font-semibold text-blue-700 dark:text-blue-300">
                      Our Promise: Draft in 3–5 days • Launch in ~10 business days.
                    </p>
                  </div>
                  <Link
                    to="/vibe-check"
                    className="inline-flex items-center justify-center w-full bg-blue-600 text-white px-8 py-3 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Start Your Project
                  </Link>
                </div>
              </div>

              {/* AI Growth */}
              <div className="relative bg-white dark:bg-gray-800 border-2 border-blue-600 dark:border-blue-500 rounded-3xl shadow-2xl overflow-hidden">
                <div className="absolute top-6 right-6">
                  <span className="inline-flex items-center px-4 py-1 rounded-full bg-blue-600 text-white text-sm font-semibold shadow-lg">
                    Most Popular
                  </span>
                </div>
                <div className="relative h-44 bg-gray-100 dark:bg-gray-700">
                  <img
                    src="/pricing-cards/2026-02-28-21-02-00-ai-growth.webp"
                    alt="AI Growth infographic showing analytics, SEO growth, and ongoing optimization"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 space-y-6">
                  <div>
                    <p className="text-sm uppercase tracking-wide text-blue-600 dark:text-blue-400 font-semibold">
                      AI Growth
                    </p>
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-white mt-2">AI Growth</h3>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-2">$250 per Month</p>
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 italic">
                      Includes the full &quot;AI Launch&quot; package. The $999 setup fee is waived with a 12-month plan.
                    </p>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300">
                    The all-in-one solution for serious businesses. You get the complete custom website plus ongoing,
                    hands-free management. We build, host, secure, and grow your site for you.
                  </p>
                  <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                    <li className="flex items-center">
                      <i className="ri-checkbox-circle-line text-blue-600 dark:text-blue-400 mr-3 text-xl" />
                      <span>Everything in the &quot;AI Launch&quot; package</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-checkbox-circle-line text-blue-600 dark:text-blue-400 mr-3 text-xl" />
                      <span>Secure, high-speed hosting so your site stays fast and online</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-checkbox-circle-line text-blue-600 dark:text-blue-400 mr-3 text-xl" />
                      <span>Daily backups and 24/7 security monitoring for total peace of mind</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-checkbox-circle-line text-blue-600 dark:text-blue-400 mr-3 text-xl" />
                      <span>2 AI-generated, SEO-optimized blog posts per month</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-checkbox-circle-line text-blue-600 dark:text-blue-400 mr-3 text-xl" />
                      <span>Monthly analytics and performance report</span>
                    </li>
                    <li className="flex items-center">
                      <i className="ri-checkbox-circle-line text-blue-600 dark:text-blue-400 mr-3 text-xl" />
                      <span>Ongoing website updates and support—just email us</span>
                    </li>
                  </ul>
                  <Link
                    to="/vibe-check"
                    className="inline-flex items-center justify-center w-full bg-blue-600 text-white px-8 py-3 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Get Started Today
                  </Link>
                </div>
              </div>
            </div>

            {/* Ecommerce add-on */}
            <div className="mt-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-8 shadow-lg">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Ecommerce Add‑On</h3>
                  <p className="mt-2 text-gray-600 dark:text-gray-300">
                    Need a storefront? Add ecommerce to your site for a flat fee.
                  </p>
                </div>
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">$1,499</div>
              </div>
              <div className="mt-4 text-gray-600 dark:text-gray-300">
                Includes product setup, payments, and a conversion‑focused checkout flow.
              </div>
            </div>

            {/* What happens next */}
            <div className="mt-12 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">What Happens Next</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm uppercase tracking-wide text-blue-600 dark:text-blue-400 font-semibold">Step 1</p>
                  <p className="mt-2 font-semibold text-gray-900 dark:text-white">5‑Minute Vibe Check</p>
                  <p className="text-gray-600 dark:text-gray-300">Share goals, style, and must‑haves.</p>
                </div>
                <div>
                  <p className="text-sm uppercase tracking-wide text-blue-600 dark:text-blue-400 font-semibold">Step 2</p>
                  <p className="mt-2 font-semibold text-gray-900 dark:text-white">15‑Minute Kickoff Call</p>
                  <p className="text-gray-600 dark:text-gray-300">We confirm the plan and timeline.</p>
                </div>
                <div>
                  <p className="text-sm uppercase tracking-wide text-blue-600 dark:text-blue-400 font-semibold">Step 3</p>
                  <p className="mt-2 font-semibold text-gray-900 dark:text-white">Draft → Launch</p>
                  <p className="text-gray-600 dark:text-gray-300">Draft in 3–5 days, launch in ~10 business days.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Still Have Questions?
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Here are the answers to the most common questions we hear from business owners like you.
              </p>
            </div>
            <div className="space-y-4">
              {[
                {
                  q: "How is this different from a DIY AI builder?",
                  a: "DIY tools still require your time for design, copy, structure, and troubleshooting. We combine AI speed with expert implementation so your site is done right and done for you.",
                },
                {
                  q: "What if I need more than 5 pages?",
                  a: "No problem. We can extend to additional pages, location pages, or full content hubs based on your growth plan.",
                },
                {
                  q: "How fast can we launch?",
                  a: "Most projects get a first draft in 3–5 days and launch in about 10 business days after approvals.",
                },
                {
                  q: "Can I keep my existing domain and branding?",
                  a: "Yes. We use your existing domain, logo, and brand colors while modernizing layout and conversion flow.",
                },
                {
                  q: "Do you support updates after launch?",
                  a: "Yes. Ongoing updates and support are included in AI Growth and available as add-ons for AI Launch.",
                },
              ].map((item, idx) => (
                <details key={idx} className="group bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6">
                  <summary className="flex cursor-pointer list-none items-center justify-between text-lg font-semibold text-gray-900 dark:text-white">
                    <span>{item.q}</span>
                    <i className="ri-add-line text-xl group-open:hidden"></i>
                    <i className="ri-subtract-line text-xl hidden group-open:block"></i>
                  </summary>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-3">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 brand-cta-surface">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to build your next website?</h2>
            <p className="text-lg md:text-xl brand-cta-copy mb-8">
              Let&apos;s take 15 minutes to talk about where you are and where you&apos;d like to go. We&apos;ll map out
              a plan that fits your goals and timeline.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/vibe-check"
                className="inline-flex items-center justify-center bg-white text-blue-600 px-8 py-3 rounded-xl text-lg font-semibold hover:bg-slate-100 transition-colors"
              >
                Start Your Project
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center justify-center bg-sky-600 text-white px-8 py-3 rounded-xl text-lg font-semibold hover:bg-sky-500 transition-colors"
              >
                Explore Our Work
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

